// src/Layout.js - Updated with Billing Dashboard menu item
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider
} from "@/components/ui/sidebar";
import { createPageUrl } from "@/utils";
import {
  Archive,
  BarChart3, // NEW: Import for billing dashboard
  Car,
  ClipboardList,
  Eye,
  LayoutDashboard,
  LogOut,
  MapPin,
  Menu,
  ParkingCircle,
  Settings,
  Shield,
  User,
  UserCog,
  Users,
  X
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";

// Define all navigation items with role restrictions
const allNavigationItems = [
  {
    title: "Dashboard",
    url: createPageUrl("Dashboard"),
    icon: LayoutDashboard,
    roles: ["admin", "operator", "viewer"]
  },
  {
    title: "Gestione Auto",
    url: createPageUrl("Auto"),
    icon: Car,
    roles: ["admin", "operator", "viewer"]
  },
  {
    title: "Archivio Auto",
    url: createPageUrl("AutoArchivio"),
    icon: Archive,
    roles: ["admin"]
  },
  // NEW: Add Billing Dashboard menu item (admin only)
  {
    title: "Dashboard Finanziaria",
    url: createPageUrl("Billing"),
    icon: BarChart3,
    roles: ["admin"]
  },
  {
    title: "Log Attività",
    url: createPageUrl("ActivityLog"),
    icon: ClipboardList,
    roles: ["admin"]
  },
  {
    title: "Gestione Clienti",
    url: createPageUrl("Clienti"),
    icon: Users,
    roles: ["admin"]
  },
  {
    title: "Gestione Posizioni",
    url: createPageUrl("Posizioni"),
    icon: MapPin,
    roles: ["admin"]
  },
  {
    title: "Gestione Stati",
    url: createPageUrl("Stati"),
    icon: Settings,
    roles: ["admin"]
  },
  {
    title: "Gestione Utenti",
    url: createPageUrl("users"),
    icon: UserCog,
    roles: ["admin"]
  }
];

export default function Layout({ children }) {
  const location = useLocation();
  const { profile, signOut, isAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Check if user is viewer
  const isViewer = profile?.role === 'viewer';
  const isOperator = profile?.role === 'operator';

  // Filter navigation items based on user role
  const getNavigationItems = () => {
    const userRole = profile?.role;
    return allNavigationItems.filter(item => item.roles.includes(userRole));
  };

  const navigationItems = getNavigationItems();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getUserInitial = () => {
    if (profile?.first_name) return profile.first_name[0].toUpperCase();
    if (profile?.email) return profile.email[0].toUpperCase();
    return 'U';
  };

  const getUserDisplayName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    if (profile?.first_name) return profile.first_name;
    return profile?.email?.split('@')[0] || 'Utente';
  };

  const getRoleDisplayName = () => {
    if (isAdmin) return 'Amministratore';
    if (isViewer) return 'Visualizzatore';
    if (isOperator) return 'Operatore';
    return 'Utente';
  };

  const getRoleIcon = () => {
    if (isAdmin) return Shield;
    if (isViewer) return Eye;
    return User;
  };

  const getRoleColorClasses = () => {
    if (isAdmin) return 'bg-purple-900/50 text-purple-200';
    if (isViewer) return 'bg-green-900/50 text-green-200';
    return 'bg-blue-900/50 text-blue-200';
  };

  const getRoleBadgeClasses = () => {
    if (isAdmin) return 'bg-purple-100 text-purple-800';
    if (isViewer) return 'bg-green-100 text-green-800';
    return 'bg-blue-100 text-blue-800';
  };

  const getCurrentPageTitle = () => {
    const currentItem = navigationItems.find(item => item.url === location.pathname);
    return currentItem ? currentItem.title : 'ParkManager';
  };

  // Close sidebar when location changes (mobile navigation)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Handle escape key to close sidebar
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    if (sidebarOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [sidebarOpen]);

  const RoleIcon = getRoleIcon();

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-gradient-to-br from-slate-50 to-slate-100 relative">
        <style>{`
          :root {
            --primary: 220 87% 23%;
            --primary-foreground: 210 40% 98%;
            --secondary: 220 14% 96%;
            --secondary-foreground: 220 9% 46%;
            --accent: 220 14% 96%;
            --accent-foreground: 220 9% 46%;
            --muted: 220 14% 96%;
            --muted-foreground: 220 9% 46%;
            --card: 0 0% 100%;
            --card-foreground: 220 9% 9%;
            --border: 220 13% 91%;
            --ring: 220 87% 23%;
          }
          
          .sidebar-gradient {
            background: linear-gradient(180deg, #1e293b 0%, #334155 100%);
          }
          
          .glass-effect {
            backdrop-filter: blur(20px);
            background: rgba(255, 255, 255, 0.9);
            border: 1px solid rgba(255, 255, 255, 0.2);
          }

          .mobile-sidebar {
            position: fixed;
            top: 0;
            left: 0;
            height: 100vh;
            width: 280px;
            z-index: 50;
            transform: translateX(-100%);
            transition: transform 0.3s ease-in-out;
          }
          
          .mobile-sidebar.open {
            transform: translateX(0);
          }
          
          .mobile-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 40;
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.3s ease-in-out;
          }
          
          .mobile-overlay.open {
            opacity: 1;
            visibility: visible;
          }

          @media (min-width: 1024px) {
            .mobile-sidebar {
              position: relative;
              transform: none !important;
              z-index: auto;
            }
            
            .mobile-overlay {
              display: none;
            }
          }
        `}</style>
        
        <div 
          className={`mobile-overlay ${sidebarOpen ? 'open' : ''} lg:hidden`}
          onClick={() => setSidebarOpen(false)}
        />
        
        <Sidebar className={`border-r-0 sidebar-gradient mobile-sidebar ${sidebarOpen ? 'open' : ''} lg:relative lg:transform-none`}>
          <SidebarHeader className="border-b border-slate-600 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                <img 
                  src="/logo.png" 
                  alt="Company Logo" 
                  className="w-10 h-10 rounded-xl object-contain shadow-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div 
                  className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg" 
                  style={{ display: 'none' }}
                >
                  <ParkingCircle className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-white text-lg">ParkManager</h2>
                <p className="text-xs text-slate-300">Gestione Parcheggio</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-slate-300 hover:text-white hover:bg-slate-600 p-1 min-w-[32px] min-h-[32px]"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </SidebarHeader>
          
          <SidebarContent className="p-3 flex-1 overflow-y-auto">
            <SidebarGroup>
              <SidebarGroupLabel className="text-xs font-medium text-slate-400 uppercase tracking-wider px-3 py-2">
                Navigazione
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-1">
                  {navigationItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton 
                        asChild 
                        className={`rounded-lg transition-all duration-200 ${
                          location.pathname === item.url 
                            ? 'bg-blue-500 text-white shadow-lg' 
                            : 'text-slate-300 hover:bg-slate-600 hover:text-white'
                        }`}
                      >
                        <Link 
                          to={item.url} 
                          className="flex items-center gap-3 px-3 py-3 min-h-[48px]"
                          onClick={() => setSidebarOpen(false)}
                        >
                          <item.icon className="w-5 h-5 flex-shrink-0" />
                          <span className="font-medium">{item.title}</span>
                          {isViewer && (item.title === 'Gestione Auto') && (
                            <span className="ml-auto text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded-full">
                              Solo lettura
                            </span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="border-t border-slate-600 p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-500 to-slate-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-medium text-sm">{getUserInitial()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-white text-sm truncate">{getUserDisplayName()}</p>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColorClasses()}`}>
                    <RoleIcon className="w-3 h-3" />
                    {getRoleDisplayName()}
                  </span>
                </div>
              </div>
            </div>
            
            <Button
              onClick={handleSignOut}
              variant="outline"
              size="sm"
              className="w-full bg-transparent border-slate-500 text-slate-300 hover:bg-slate-600 hover:text-white hover:border-slate-400 transition-all duration-200 min-h-[48px]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Disconnetti
            </Button>
          </SidebarFooter>
        </Sidebar>

        <main className="flex-1 flex flex-col min-w-0">
          <header className="bg-white/95 backdrop-blur-sm border-b border-slate-200 px-4 py-3 lg:hidden sticky top-0 z-30">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost" 
                size="sm"
                className="hover:bg-slate-100 p-2 rounded-lg transition-colors duration-200 min-h-[48px] min-w-[48px] flex items-center justify-center"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </Button>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-bold text-slate-800 truncate">{getCurrentPageTitle()}</h1>
                <p className="text-sm text-slate-600 truncate">{getRoleDisplayName()}</p>
              </div>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getRoleBadgeClasses()}`}>
                <RoleIcon className="w-3 h-3" />
                {isAdmin ? 'Admin' : isViewer ? 'Viewer' : 'Operatore'}
              </span>
            </div>
          </header>

          <header className="hidden lg:block bg-white/80 backdrop-blur-sm border-b border-slate-200 px-6 py-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <h1 className="text-xl font-bold text-slate-800">{getCurrentPageTitle()}</h1>
                <p className="text-sm text-slate-600">{getRoleDisplayName()}</p>
              </div>
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeClasses()}`}>
                <RoleIcon className="w-4 h-4" />
                {getRoleDisplayName()}
              </span>
            </div>
          </header>

          <div className="flex-1 overflow-auto">
            {children}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}